import React from 'react';
import Layout from '../Layout';
import StudentCompetencyMeter from '../Components/StudentCompetencyMeter';
import { Award, TrendingUp, Target } from 'lucide-react';

const MyCompetencies = () => {
    return (
        <Layout>
            <div className="bg-gradient-to-br from-orange-50 via-white to-red-50 min-h-full" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" />
                
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header Section */}
                    <div className="pt-16 pb-8 text-center relative overflow-hidden">
                        <div className="absolute inset-0"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-center gap-3 mb-4">
                                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#D67C2C] to-[#FF7A59] flex items-center justify-center">
                                    <Award className="w-8 h-8 text-white" />
                                </div>
                            </div>
                            <h1 className="text-5xl font-bold bg-gradient-to-r from-[#D67C2C] to-[#FF7A59] bg-clip-text text-transparent mb-4">
                                My Competencies
                            </h1>
                            <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                                Track your learning progress and see how you're developing across different skill areas
                            </p>
                        </div>
                    </div>

                    {/* Quick Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Learning Progress</p>
                                        <p className="text-2xl font-bold text-[#D67C2C] mt-1">Track Your Growth</p>
                                    </div>
                                    <div className="p-3 rounded-full bg-orange-100">
                                        <TrendingUp className="w-6 h-6 text-[#D67C2C]" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Skill Domains</p>
                                        <p className="text-2xl font-bold text-blue-600 mt-1">Multiple Areas</p>
                                    </div>
                                    <div className="p-3 rounded-full bg-blue-100">
                                        <Target className="w-6 h-6 text-blue-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Achievements</p>
                                        <p className="text-2xl font-bold text-green-600 mt-1">Celebrate Success</p>
                                    </div>
                                    <div className="p-3 rounded-full bg-green-100">
                                        <Award className="w-6 h-6 text-green-600" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Competency Meter */}
                    <StudentCompetencyMeter className="shadow-xl" />

                    {/* Information Section */}
                    <div className="mt-10 bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
                        <h2 className="text-2xl font-bold text-gray-900 font-[poppins] mb-6">
                            Understanding Your Competency Progress
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 font-[poppins] mb-4">
                                    What are Competencies?
                                </h3>
                                <p className="text-gray-600 font-[poppins] mb-4">
                                    Competencies are the skills, knowledge, and abilities you develop as you learn. 
                                    They help you understand what you can do and what you're working towards.
                                </p>
                                <ul className="space-y-2 text-gray-600 font-[poppins]">
                                    <li className="flex items-start gap-2">
                                        <div className="w-2 h-2 bg-[#D67C2C] rounded-full mt-2 flex-shrink-0"></div>
                                        <span>Track your progress across different learning areas</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-2 h-2 bg-[#D67C2C] rounded-full mt-2 flex-shrink-0"></div>
                                        <span>See which skills you've mastered and which need more practice</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-2 h-2 bg-[#D67C2C] rounded-full mt-2 flex-shrink-0"></div>
                                        <span>Celebrate your achievements and set new learning goals</span>
                                    </li>
                                </ul>
                            </div>
                            
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 font-[poppins] mb-4">
                                    Learning Domains
                                </h3>
                                <p className="text-gray-600 font-[poppins] mb-4">
                                    Your learning is organized into different domains, each focusing on important skills:
                                </p>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="w-8 h-8 bg-[#FF7A59] rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm font-bold">P</span>
                                        </div>
                                        <span className="text-gray-700 font-[poppins] text-sm">Physical Development</span>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="w-8 h-8 bg-[#F17C45] rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm font-bold">S</span>
                                        </div>
                                        <span className="text-gray-700 font-[poppins] text-sm">Socio-Emotional Development</span>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="w-8 h-8 bg-[#1D3469] rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm font-bold">C</span>
                                        </div>
                                        <span className="text-gray-700 font-[poppins] text-sm">Cognitive Development</span>
                                    </div>
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                        <div className="w-8 h-8 bg-[#FF7A59] rounded-lg flex items-center justify-center">
                                            <span className="text-white text-sm font-bold">L</span>
                                        </div>
                                        <span className="text-gray-700 font-[poppins] text-sm">Language & Literacy Development</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default MyCompetencies;